// Home.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import "./Home.css";

// ✅ feature icons (png)
import icon1 from "../assets/icon.png";
import icon2 from "../assets/icon-2.png";
import icon3 from "../assets/icon-3.png";
import icon4 from "../assets/icon4.png";

// ✅ IBS section images
// import session1 from "../assets/session1.jpg";
import session2 from "../assets/session2.jpg";

// ✅ Company projects icon (your logo)

// ✅ Core image
import coreImage from "../assets/Core-Image.jpg";

import buildingSecondary from "../assets/building7.png";
import buildingMain from "../assets/building3.jpg";

// ✅ YouTube link (opens on click/tap)

/* =========================================================
   ✅ Count-up support (in-view once + reduced-motion safe)
   ========================================================= */

function useInViewOnce({ threshold = 0.35 } = {}) {
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion) {
      setInView(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [prefersReducedMotion, threshold]);

  return { ref, inView };
}

function useCountUp({ end = 0, start = 0, duration = 1200, play = false }) {
  const prefersReducedMotion = useReducedMotion();
  const [value, setValue] = useState(start);

  useEffect(() => {
    if (!play) return;

    if (prefersReducedMotion) {
      setValue(end);
      return;
    }

    let raf = 0;
    const from = start;
    const to = end;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);

      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      const current = Math.round(from + (to - from) * eased);

      setValue(current);

      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [play, end, start, duration, prefersReducedMotion]);

  return value;
}

export default function Home() {
  const heroImageUrl = buildingMain;

  const heroItems = useMemo(
    () => [
      {
        title: "Built On Strong Foundations",
        subtitle:
          "With disciplined planning, traditional design values, and high-quality materials, IBS delivers infrastructure that stands the test of time.",
      },
      {
        title: "Where Planning Meets Craft",
        subtitle:
          "Our work integrates analytical planning methods, established design traditions, and carefully sourced materials to support durable infrastructure development.",
      },
    ],
    []
  );

  const [heroIndex, setHeroIndex] = useState(0);
  const activeHero = heroItems[heroIndex] || heroItems[0];
  const heroRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroLeftY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, prefersReducedMotion ? 0 : 80]
  );
  const heroLeftScale = useTransform(
    scrollYProgress,
    [0, 1],
    [1, prefersReducedMotion ? 1 : 1.08]
  );
  const heroRightY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, prefersReducedMotion ? 0 : -60]
  );
  const heroRightScale = useTransform(
    scrollYProgress,
    [0, 1],
    [1, prefersReducedMotion ? 1 : 1.06]
  );
  const heroContentY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, prefersReducedMotion ? 0 : 25]
  );

  const springConfig = prefersReducedMotion
    ? { stiffness: 200, damping: 40, mass: 1 }
    : { stiffness: 120, damping: 22, mass: 0.6 };

  const heroLeftYSmooth = useSpring(heroLeftY, springConfig);
  const heroLeftScaleSmooth = useSpring(heroLeftScale, springConfig);
  const heroRightYSmooth = useSpring(heroRightY, springConfig);
  const heroRightScaleSmooth = useSpring(heroRightScale, springConfig);
  const heroContentYSmooth = useSpring(heroContentY, springConfig);

  const coreFeatures = useMemo(
    () => [
      {
        id: "01",
        title: "Early Engagement",
        body:
          "Early engagement reduces project uncertainties through comprehensive scope definition and systems optimisation. The earlier you bring IBS on board, the smoother and faster delivery becomes.",
      },
      {
        id: "02",
        title: "Integrity",
        body:
          "We deliver exactly what we commit to — transparent budgets, honest timelines, and clear communication from start to finish.",
      },
      {
        id: "03",
        title: "Solutions",
        body:
          "We solve onsite challenges with practical engineering thinking and coordinated execution across architecture, structure, and services.",
      },
      {
        id: "04",
        title: "Innovation",
        body:
          "Modern planning, smarter workflows, and updated construction techniques that reduce waste and speed up completion.",
      },
    ],
    []
  );

  const [activeCore, setActiveCore] = useState("01");
  const activeItem =
    coreFeatures.find((x) => x.id === activeCore) || coreFeatures[0];

  // ✅ popup modal
  const [isEstimatorOpen, setIsEstimatorOpen] = useState(false);

  // ✅ demo form states
  const [houseArea, setHouseArea] = useState("");
  const [room, setRoom] = useState("");
  const [bathroom, setBathroom] = useState("");
  const [material, setMaterial] = useState("grey"); // grey | premium

  // ✅ lock background scroll when modal open
  useEffect(() => {
    if (isEstimatorOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isEstimatorOpen]);

  // ✅ close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIsEstimatorOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (heroItems.length < 2) return undefined;
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroItems.length);
    }, 5200);
    return () => clearInterval(interval);
  }, [heroItems.length]);

  // ✅ simple price output
  const estimatedPrice = material === "premium" ? 95000 : 80000;
  const prefersReducedMotionList = useReducedMotion();

  // ✅ Refactored Hero Content Render
  const renderHeroContent = (isOverlay = false) => (
    <motion.div
      className="heroSplit__contentInner"
      style={{ y: heroContentYSmooth }}
      aria-hidden={isOverlay ? "true" : undefined}
    >
      <p className="heroSplit__eyebrow">Modern Construction</p>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeHero.title}
          className="heroSplit__carouselItem"
          initial={prefersReducedMotion ? "static" : "hidden"}
          animate="show"
          exit={prefersReducedMotion ? "static" : "exit"}
          variants={{
            hidden: { opacity: 0 },
            show: {
              opacity: 1,
              transition: {
                staggerChildren: 0.18,
                delayChildren: 0.08,
              },
            },
            exit: {
              opacity: 0,
              transition: { duration: 0.35, ease: "easeInOut" },
            },
            static: { opacity: 1 },
          }}
        >
          <motion.h1
            id={isOverlay ? undefined : "hero-title"}
            className="heroSplit__title"
            variants={{
              hidden: { opacity: 0, x: -80 },
              show: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.55, ease: "easeOut" },
              },
              exit: { opacity: 0, x: -48, transition: { duration: 0.3 } },
              static: { opacity: 1, x: 0 },
            }}
          >
            {activeHero.title}
          </motion.h1>

          <motion.p
            className="heroSplit__subtitle"
            variants={{
              hidden: { opacity: 0, x: -72 },
              show: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.5, ease: "easeOut" },
              },
              exit: { opacity: 0, x: -42, transition: { duration: 0.28 } },
              static: { opacity: 1, x: 0 },
            }}
          >
            {activeHero.subtitle}
          </motion.p>
        </motion.div>
      </AnimatePresence>

      <div className="heroSplit__ctaRow">
        <a className="heroSplit__cta" href="/contact">
          Request a Quote
        </a>
      </div>
    </motion.div>
  );

  return (
    <>
      {/* ✅ HERO */}
      <section
        className="heroSplit"
        aria-labelledby="hero-title"
        ref={heroRef}
        style={{
          "--hero-left-image": `url("${heroImageUrl}")`,
          "--hero-right-image": `url("${buildingSecondary}")`,
        }}
      >
        <motion.div
          className="heroSplit__media heroSplit__media--left"
          aria-hidden="true"
          style={{ y: heroLeftYSmooth, scale: heroLeftScaleSmooth }}
        />
        <motion.div
          className="heroSplit__media heroSplit__media--right"
          aria-hidden="true"
          style={{ y: heroRightYSmooth, scale: heroRightScaleSmooth }}
        />

        <motion.div
          className="heroSplit__mediaFrame heroSplit__mediaFrame--left"
          aria-hidden="true"
          initial={
            prefersReducedMotion
              ? { opacity: 0.7 }
              : { opacity: 0.4, scale: 0.98 }
          }
          animate={
            prefersReducedMotion
              ? { opacity: 0.7 }
              : { opacity: [0.45, 0.85, 0.45], scale: [0.98, 1, 0.98] }
          }
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: 5.4, repeat: Infinity, ease: "easeInOut" }
          }
        />
        <motion.div
          className="heroSplit__mediaFrame heroSplit__mediaFrame--right"
          aria-hidden="true"
          initial={
            prefersReducedMotion
              ? { opacity: 0.7 }
              : { opacity: 0.4, scale: 0.98 }
          }
          animate={
            prefersReducedMotion
              ? { opacity: 0.7 }
              : { opacity: [0.45, 0.85, 0.45], scale: [0.98, 1, 0.98] }
          }
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : {
                duration: 5.4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.6,
              }
          }
        />

        <div className="heroSplit__line heroSplit__line--top" aria-hidden="true" />
        <div className="heroSplit__line heroSplit__line--bottom" aria-hidden="true" />

        <motion.div
          className="heroSplit__motionFrame"
          aria-hidden="true"
          initial={
            prefersReducedMotion
              ? { opacity: 0.5, scale: 1 }
              : { opacity: 0, scale: 0.96 }
          }
          animate={
            prefersReducedMotion
              ? { opacity: 0.5, scale: 1 }
              : { opacity: [0.35, 0.7, 0.35], scale: [0.98, 1, 0.98] }
          }
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: 6, repeat: Infinity, ease: "easeInOut" }
          }
        />

        {/* Base Content Layer */}
        <div className="heroSplit__content">{renderHeroContent()}</div>

        {/* Overlay Content Layer (White Text, Clipped - Top/Right Image) */}
        <div className="heroSplit__overlayWrapper" aria-hidden="true">
          <div className="heroSplit__content">{renderHeroContent(true)}</div>
        </div>

        {/* Overlay Content Layer (White Text, Clipped - Bottom/Left Image) */}
        <div
          className="heroSplit__overlayWrapper heroSplit__overlayWrapper--bottom"
          aria-hidden="true"
        >
          <div className="heroSplit__content">{renderHeroContent(true)}</div>
        </div>
      </section>

      {/* ✅ POPUP MODAL */}
      {isEstimatorOpen && (
        <div
          className="estModalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label="Estimated Price"
          onMouseDown={(e) => {
            if (e.target.classList.contains("estModalOverlay")) {
              setIsEstimatorOpen(false);
            }
          }}
        >
          <div className="estModalCard" role="document">
            <button
              type="button"
              className="estModalClose"
              aria-label="Close"
              onClick={() => setIsEstimatorOpen(false)}
            >
              ×
            </button>

            <div className="estModalHeader">
              <h2 className="estModalTitle">Estimated Price</h2>
              <p className="estModalSub">
                To get the cost estimation of your house please select from the
                following:
              </p>
            </div>

            <div className="estModalBody">
              <div className="estForm">
                <div className="estField">
                  <label className="estLabel">House Area</label>
                  <div className="estSelectWrap">
                    <select
                      className="estSelect"
                      value={houseArea}
                      onChange={(e) => setHouseArea(e.target.value)}
                    >
                      <option value="">Choose Area</option>
                      <option value="800-1200">800 - 1200</option>
                      <option value="1200-1800">1200 - 1800</option>
                      <option value="1800-2400">1800 - 2400</option>
                    </select>
                    <span className="estCaret" aria-hidden="true" />
                  </div>
                </div>

                <div className="estGrid2">
                  <div className="estField">
                    <label className="estLabel">Room</label>
                    <div className="estSelectWrap">
                      <select
                        className="estSelect"
                        value={room}
                        onChange={(e) => setRoom(e.target.value)}
                      >
                        <option value="">Select Room</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                      </select>
                      <span className="estCaret" aria-hidden="true" />
                    </div>
                  </div>

                  <div className="estField">
                    <label className="estLabel">Bathroom</label>
                    <div className="estSelectWrap">
                      <select
                        className="estSelect"
                        value={bathroom}
                        onChange={(e) => setBathroom(e.target.value)}
                      >
                        <option value="">Select Bathroom</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                      </select>
                      <span className="estCaret" aria-hidden="true" />
                    </div>
                  </div>
                </div>

                <div className="estField">
                  <label className="estLabel">Building Materials</label>
                  <div className="estToggleRow">
                    <button
                      type="button"
                      className={`estToggle ${material === "grey" ? "estToggleActive" : ""
                        }`}
                      onClick={() => setMaterial("grey")}
                    >
                      A+ Grey Structure
                    </button>

                    <button
                      type="button"
                      className={`estToggle ${material === "premium" ? "estToggleActive" : ""
                        }`}
                      onClick={() => setMaterial("premium")}
                    >
                      Premium Finishing
                    </button>
                  </div>
                </div>

                <div className="estDivider" />

                <p className="estResultKicker">
                  {material === "premium"
                    ? "Premium Finishing Tentative Amount"
                    : "A+ Grey Structure Tentative Amount"}
                </p>

                <div className="estPriceBox">
                  <div className="estPriceLabel">Estimated Price:</div>
                  <div className="estPriceValue">${estimatedPrice}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ 3 FEATURE CARDS */}
      <section className="section featuresTriSection">
        <div className="container">
          <motion.div
            className="featuresTriGrid"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={{
              hidden: {},
              show: {
                transition: prefersReducedMotionList
                  ? { duration: 0 }
                  : { staggerChildren: 0.12, delayChildren: 0.1 },
              },
            }}
          >
            {/* 1) DESIGN */}
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 30, scale: 0.98 },
                show: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: prefersReducedMotionList
                    ? { duration: 0 }
                    : { duration: 0.6, ease: "easeOut" },
                },
              }}
              whileHover={
                prefersReducedMotionList
                  ? undefined
                  : { y: -6, scale: 1.02, transition: { duration: 0.2 } }
              }
            >
              <TriCard
                title="Design"
                iconSrc={icon2}
                iconAlt="Design icon"
                desc="Our sustainable designs unite aesthetics and performance through smart planning and energy-efficient solutions, creating durable, eco-conscious spaces that optimize comfort and efficiency."
              />
            </motion.div>
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 30, scale: 0.98 },
                show: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: prefersReducedMotionList
                    ? { duration: 0 }
                    : { duration: 0.6, ease: "easeOut" },
                },
              }}
              whileHover={
                prefersReducedMotionList
                  ? undefined
                  : { y: -6, scale: 1.02, transition: { duration: 0.2 } }
              }
            >
              <TriCard
                title="Core Planning"
                iconSrc={icon1}
                iconAlt="Core Planning icon"
                desc="We utilize advanced project management tools to maintain precise data control and professional workflows, ensuring every detail is tracked for efficient execution and success."
              />
            </motion.div>

            {/* 3) CONSTRUCTION */}
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 30, scale: 0.98 },
                show: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: prefersReducedMotionList
                    ? { duration: 0 }
                    : { duration: 0.6, ease: "easeOut" },
                },
              }}
              whileHover={
                prefersReducedMotionList
                  ? undefined
                  : { y: -6, scale: 1.02, transition: { duration: 0.2 } }
              }
            >
              <TriCard
                title="Construction"
                iconSrc={icon4}
                iconAlt="Construction icon"
                desc="We adhere to the highest standards of construction, utilizing sustainable materials and modern techniques to build structures that are safe, strong, and enduring."
              />
            </motion.div>

            {/* 4) QUALITY */}
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 30, scale: 0.98 },
                show: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: prefersReducedMotionList
                    ? { duration: 0 }
                    : { duration: 0.6, ease: "easeOut" },
                },
              }}
              whileHover={
                prefersReducedMotionList
                  ? undefined
                  : { y: -6, scale: 1.02, transition: { duration: 0.2 } }
              }
            >
              <TriCard
                title="Quality"
                iconSrc={icon3}
                iconAlt="Quality icon"
                desc="IBS provides uncompromising quality assurance, ensuring every project speaks to the user's needs with precision, transparency, and excellence."
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ✅ IBS SECTION */}
      <section className="section ibsAltSection">
        <div className="container">
          <div className="ibsAltWrap">
            <div className="ibsAltAccent" aria-hidden="true" />

            <div className="ibsAltRow">
              <figure className="ibsAltMedia">
                {/* <div className="ibsAltFrame">
                  <img
                    className="ibsAltImg"
                    src={session1}
                    alt="IBS construction site"
                    loading="lazy"
                  />
                </div> */}
              </figure>

              <div className="ibsAltText">
                <p className="ibsEyebrow">WELCOME TO OUR IBS</p>
                <h2 className="ibsHeadline">
                  IBS ensures project <br /> excellence
                </h2>
                <div className="ibsMiniLine" aria-hidden="true" />
              </div>
            </div>

            <div className="ibsAltRow ibsAltRowSwap">
              <div className="ibsAltText">
                <p className="ibsPara">
                  At IBS (Infrastructures of Bhargava and Suhas), we confidently
                  handle projects of all complexities, backed by long-term
                  guarantees and a commitment to adopting the latest
                  technologies. Our portfolio features a wide range of
                  successfully completed multi-storey buildings, each showcasing
                  superior finishes and high-quality construction. Building
                  homes isn't just our work—it’s our passion and purpose.
                </p>

                <div className="ibsCtaRow">
                  <NavLink to="/about" className="ibsCtaBtn">
                    Learn More <span className="ibsCtaArrow">→</span>
                  </NavLink>
                </div>
              </div>

              <figure className="ibsAltMedia">
                <div className="ibsAltFrame">
                  <img
                    className="ibsAltImg"
                    src={session2}
                    alt="IBS engineer working on site"
                    loading="lazy"
                  />
                </div>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ STATS */}
      <section className="section ibsStatsSection">
        <div className="container">
          <div className="ibsStatsGrid">
            <StatsCard big="10+" label="YEARS" sub="Professional Experience" />
            <StatsCard big="20+" label="PEOPLE" sub="Employees in team" center />
            <StatsCard big="10+" label="LOCATIONS" sub="Sites in development" />
          </div>
        </div>
      </section>



      {/* ✅ CORE FEATURES */}
      <section className="section coreFeaturesSection">
        <div className="container">
          <div className="coreFeaturesGrid">
            <div className="coreLeft">
              <p className="coreKicker">CORE FEATURES</p>
              <h2 className="coreTitle">
                What Makes Us <br /> Different
              </h2>

              <div className="coreList" role="tablist">
                {coreFeatures.map((item) => {
                  const isActive = item.id === activeCore;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`coreItem ${isActive ? "coreItemActive" : ""}`}
                      onClick={() => setActiveCore(item.id)}
                      role="tab"
                      aria-selected={isActive}
                    >
                      <span className="coreNum">{item.id}.</span>
                      <span className="coreItemTitle">{item.title}</span>
                      <span className="coreSlash" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>

              <div className="coreDetailCard" role="tabpanel">
                <div className="coreDetailHeader">
                  <span className="coreDetailPill">{activeItem.id}</span>
                  <h3 className="coreDetailTitle">{activeItem.title}</h3>
                </div>
                <p className="coreDetailText">{activeItem.body}</p>
              </div>
            </div>

            <div className="coreRight">
              <div className="coreMedia">
                <img
                  src={coreImage}
                  alt="Core features construction"
                  className="coreImg"
                  loading="lazy"
                />
                <div className="coreGreenWedge" aria-hidden="true" />
              </div>
            </div>
          </div>

          {/* ✅ NEW SECTION: WHAT DO WE OFFER IN CONSTRUCTION */}
          <div className="offerWrap" aria-label="What do we offer in Construction">
            <div className="offerCard">
              <p className="offerKicker">CONSTRUCTION</p>
              <h3 className="offerTitle">What do we offer in Construction</h3>
              <div className="offerDivider" aria-hidden="true" />

              {/* ✅ One by one list + bullet instead of numbers */}
              <ul className="offerGrid">
                <li className="offerItem">
                  <span className="offerBullet" aria-hidden="true" />
                  <span className="offerText">Infrastructure Projects</span>
                </li>

                <li className="offerItem">
                  <span className="offerBullet" aria-hidden="true" />
                  <span className="offerText">Residential Projects</span>
                </li>

                <li className="offerItem">
                  <span className="offerBullet" aria-hidden="true" />
                  <span className="offerText">Commercial Projects</span>
                </li>

                <li className="offerItem">
                  <span className="offerBullet" aria-hidden="true" />
                  <span className="offerText">Renovation Projects</span>
                </li>
              </ul>

              <span className="offerNoise" aria-hidden="true" />
              <span className="offerGlow" aria-hidden="true" />
            </div>
          </div>

        </div>
      </section>
    </>
  );
}

function TriCard({ title, desc, iconSrc, iconAlt }) {
  return (
    <article className="triCard triCardModern">
      <div className="triTopAccent" aria-hidden="true" />
      <div className="triIconBox">
        <img className="triIconImg" src={iconSrc} alt={iconAlt} loading="lazy" />
      </div>
      <h3 className="triTitle">{title}</h3>
      <div className="triBodyPanel">
        <p className="triDesc">{desc}</p>
      </div>
    </article>
  );
}

function StatsCard({ big, label, sub, center }) {
  const parsed = useMemo(() => {
    const str = String(big ?? "");
    const match = str.match(/(\d+)/);
    const num = match ? parseInt(match[1], 10) : 0;
    const suffix = str.replace(String(match?.[1] ?? ""), "");
    return { num, suffix };
  }, [big]);

  const { ref, inView } = useInViewOnce({ threshold: 0.35 });

  const count = useCountUp({
    start: 0,
    end: parsed.num,
    duration: 1200,
    play: inView,
  });

  const display = inView ? `${count}${parsed.suffix}` : big;

  return (
    <article
      ref={ref}
      className={`ibsStatNeo ${center ? "ibsStatNeoCenter" : ""}`}
    >
      <div className="ibsStatNeoTop" aria-hidden="true" />
      <div className="ibsStatNeoCorner" aria-hidden="true" />
      <div className="ibsStatNeoGrid">
        <div className="ibsStatNeoNum">{display}</div>
        <div className="ibsStatNeoRight">
          <div className="ibsStatNeoChip">{label}</div>
          <p className="ibsStatNeoSub">{sub}</p>
        </div>
      </div>
    </article>
  );
}

// function ProjectCard({ imgSrc, title, desc, tag }) {
//   return (
//     <article className="projectShotCard projectShotCompact">
//       <div className="projectShotMedia">
//         <img className="projectShotImg" src={imgSrc} alt={title} loading="lazy" />
//         <div className="projectShotTag">{tag}</div>
//       </div>

//       <div className="projectShotBottom">
//         <span className="projectShotMint" aria-hidden="true" />
//         <div className="projectShotPanel">
//           <h3 className="projectShotTitle">{title}</h3>
//           <p className="projectShotDesc">{desc}</p>
//         </div>
//       </div>
//     </article>
//   );
// }
